import { identity } from "lodash-es";

import { getApiBaseUrl, getBaseStore } from "@utils/configuration/aem";

import { translateKey } from "@redux/modules/value/translations/translations.helpers";

import { head } from "./array";

const BASE = "ApiErrors";

const HTTP_CODES: Record<string, string> = {
    "400": "badRequest",
    "401": "unAuthorized",
    "403": "forbidden",
    "404": "notFound",
    "405": "notAllowed",
    "5xx": "serviceUnavailable",
};

const HTTP_METHODS: Record<string, string> = {
    GET: "retrieve",
    POST: "submit",
    PUT: "update",
    DELETE: "delete",
    PATCH: "edit",
};

export const shouldSkip = (
    errorsToSkip: Array<number> = [],
    status: number,
): boolean => {
    return errorsToSkip.some((e: any) => e === status);
};

export const getErrorMessage = (
    translations: Record<string, string>,
    data: string,
    url: string,
    method: string,
    status: number,
): string => {
    const urlParts: Array<string> = url
        .replaceAll(`${getBaseStore()}/`, "")
        .replaceAll(`${getApiBaseUrl()}/`, "")
        .replaceAll(`web/`, "")
        .split("?")[0]
        .split("/")
        .filter(identity);

    if (data) {
        return translateKey(translations, `apiMessages.title.${data}`);
    }

    const serviceNameKey = getServiceName(urlParts);
    const endPointKey = getEndpoint(urlParts);
    const methodKey = getMethodKey(method);
    const statusCodeKey = getCodeKey(status);

    const keys = generateKeys(
        statusCodeKey,
        serviceNameKey,
        endPointKey,
        methodKey,
    );

    const translationKey = getI18nKey(keys, translations);

    return translationKey
        ? translateKey(translations, translationKey)
        : `Error @ ${url} :: Implement translationkey or variants :: ${head(
              keys,
          )}`;
};

const getServiceName = (url: Array<string>) => url.at(0);

const getEndpoint = (url: Array<string>) => url.slice(1).join(".");

const getMethodKey = (method: string): string | undefined =>
    HTTP_METHODS[method.toUpperCase()];

const getCodeKey = (status: number): string =>
    HTTP_CODES[status >= 500 ? "5xx" : status.toString()];

const composeKey = (
    keyParts: Array<string | undefined>,
    statusCode?: string,
) => {
    const keyWithoutStatusCode =
        BASE +
        keyParts
            .filter(identity)
            .map(part => `.${part}`)
            .join("");

    return statusCode
        ? `${keyWithoutStatusCode}.${statusCode}`
        : keyWithoutStatusCode;
};

const generateKeys = (
    statusCode: string,
    serviceName?: string,
    endPoint?: string,
    method?: string,
) => [
    composeKey([serviceName, endPoint, method], statusCode),
    composeKey([serviceName, endPoint], statusCode),
    composeKey([serviceName, endPoint, method]),
    composeKey([serviceName, endPoint]),
    composeKey([serviceName], statusCode),
    composeKey([], statusCode),
];

const getI18nKey = (
    keys: Array<string>,
    translations: Record<string, string>,
): string | void =>
    keys.find(
        key =>
            !!Object.keys(translations || {}).find(
                k => k.toLowerCase() === key.toLowerCase(),
            ),
    );
